import dayjs from "dayjs"
import { ref, computed } from "vue"
import { useAuth } from "../composition-api/useAuth"
import { setError, setSuccess } from "./Notification"
import { sortTimeEntries, ensureTimeEntryExtensions } from "../utils/timeEntryExtensions"
import { insertNewTimeEntryAsync, getEntityAsync, saveWorkOrderTimesAsync } from "../api"

// split out of components/WorkOrderTimes.js, which is now a `.vue`: a `<script setup>` block runs
// per instance and cannot carry a module-level composable alongside the component
export const useTimeEntries = () => {
  const { fetchAuthAsync } = useAuth()

  const timeEntries = ref([])

  const loadTimeEntriesAsync = async (workOrderType, workOrderId) => {
    const result = await getEntityAsync(fetchAuthAsync, `WorkOrderTime/${workOrderType}`, workOrderId)
    timeEntries.value = sortTimeEntries(result ?? [])
    populateTimeExtensions()
  }

  const populateTimeExtensions = () => {
    timeEntries.value = timeEntries.value.map((timeEntry) => {
      ensureTimeEntryExtensions(timeEntry)
      return timeEntry
    })
  }

  const floorMinutes = (date, minutes) => {
    if (!minutes) {
      return date
    }
    const m = date.minute()
    const f = m - (m % minutes)
    return date.minute(f)
  }

  const ceilingMinutes = (date, minutes) => {
    if (!minutes) {
      return date
    }
    const m = date.minute()
    const f = m + (minutes - (m % minutes))
    return date.minute(f)
  }

  const newTimeEntryAsync = async (workOrderType, workOrderId, startTimeObject, bottomMinutes = 0) => {
    if (!workOrderType) {
      throw new Error("missing work order type")
    }
    if (!workOrderId) {
      throw new Error("missing workOrderId")
    }
    if (!Number.isInteger(bottomMinutes) || bottomMinutes < 0) {
      throw new Error("missing addMinutes")
    }
    try {
      if (!dayjs.isDayjs(startTimeObject)) {
        startTimeObject = dayjs.utc()
      }
      await insertNewTimeEntryAsync(
        fetchAuthAsync,
        workOrderType,
        workOrderId,
        floorMinutes(startTimeObject, bottomMinutes),
      )
      await loadTimeEntriesAsync(workOrderType, workOrderId)
      setSuccess("Delo začeto.", true /* skip scroll */)
    } catch (err) {
      setError(err)
    }
  }

  const closeTimeEntryAsync = async (te, workOrderType, workOrderId, topMinutes = 0) => {
    if (!te) {
      throw new Error("trying to close null time entry")
    }
    if (!!te.endDateTime) {
      throw new Error("timeEntry alread closed")
    }
    if (!workOrderType) {
      throw new Error("missing work order type")
    }
    if (!workOrderId) {
      throw new Error("missing workOrderId")
    }
    if (!Number.isInteger(topMinutes) || topMinutes < 0) {
      throw new Error("missing addMinutes")
    }
    const now = dayjs()
      .utc()
      .year(te.startDateTimeObject.year())
      .month(te.startDateTimeObject.month())
      .date(te.startDateTimeObject.date())
    console.log(now)
    te.endDateTime = ceilingMinutes(now, topMinutes).format()
    te.isDirty = true
    try {
      const result = await saveWorkOrderTimesAsync(fetchAuthAsync, te, workOrderType, workOrderId)
      if (result) {
        await loadTimeEntriesAsync(workOrderType, workOrderId)
        setSuccess("Delo zaključeno.", true /* skip scroll */)
        te.isDirty = false
        return te
      } else {
        setError("Ni spremembe")
      }
    } catch (err) {
      setError(err)
    }
    return null
  }

  const openTimeRecord = computed(() => timeEntries.value.find((t) => !t.endDateTimeObject))

  const removeTimeEntry = (te) => (te.isForDelete = true)

  return {
    closeTimeEntryAsync,
    newTimeEntryAsync,
    loadTimeEntriesAsync,
    openTimeRecord,
    populateTimeExtensions,
    removeTimeEntry,
    timeEntries,
  }
}
